import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { Form, Formik } from 'formik';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { UserPlus } from 'lucide-react';
import { useStore } from '../../../../hooks/useStore';
import FormPopupFooter from '../../../../components/form-components/common-components/FormPopupFooter';
import { PersonSchema } from '../../../../schemas/PersonSchema';
import { PersonFormValues, personInitialValues } from '../../../../models/person/PersonModel';
import { toast } from 'react-toastify';
import PersonFormFields from './PersonFormFields';
import styles from './PersonForm.module.css';

const NewPersonPopup = () => {
  const { popupStore, hospitalStore, uiStore, personStore } = useStore();

  useEffect(() => {
    const fetchHospitals = async () => {
      if (!popupStore.isNewPersonOpen) {
        return;
      }

      try {
        uiStore.setLoading(true);
        await hospitalStore.listAllHospitals();
      } catch (err) {
        console.error('Error fetching hospitals:', err);
      } finally {
        uiStore.setLoading(false);
      }
    };

    void fetchHospitals();
  }, [hospitalStore, uiStore, popupStore.isNewPersonOpen]);

  const hospitalOptions = useMemo(
    () =>
      hospitalStore.hospitalList
        .filter(
          (hospital): hospital is { id: number; hospitalName: string } =>
            hospital.id !== undefined && hospital.hospitalName !== undefined
        )
        .map(hospital => ({
          value: hospital.id,
          label: hospital.hospitalName,
        })),
    [hospitalStore.hospitalList]
  );

  const handleSubmit = async (values: PersonFormValues) => {
    if (!values.hospitalId) {
      toast.error('Lütfen bir hastane seçiniz');
      return;
    }

    const response = await personStore.createPerson(values);
    if (response.error) {
      toast.error('Kişi oluşturulamadı. ' + response.message);
      return;
    }

    toast.success('Kişi başarıyla oluşturuldu');
    popupStore.setIsNewPersonOpen(false);
  };

  const handleClose = () => {
    popupStore.setIsNewPersonOpen(false);
    toast.dismiss();
  };

  if (!popupStore.isNewPersonOpen) {
    return null;
  }

  return (
    <Formik
      initialValues={personInitialValues}
      validationSchema={PersonSchema}
      onSubmit={handleSubmit}
    >
      {() => (
        <Popup
          isOpen={popupStore.isNewPersonOpen}
          onClose={handleClose}
          title="Yeni Kişi Oluştur"
          description="Yeni kişi bilgilerini girin"
          variant="success"
          icon={<UserPlus size={22} strokeWidth={2} />}
          footer={() => <FormPopupFooter onClose={handleClose} />}
        >
          <Form className={styles.form}>
            <PersonFormFields hospitalOptions={hospitalOptions} />
          </Form>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(NewPersonPopup);
